import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Star, Mail, Trash2, Pencil, Upload, X, Send, UserCheck, Tag, Check, Music2, CalendarClock, ShieldCheck, Smartphone } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import ImportContactsModal from "../components/ImportContactsModal";

/**
 * Contacts page — your private rolodex.
 * - List, search, favourite, group, edit, delete contacts.
 * - Send a 1:1 or group email.
 * - Paste-import a block of names + emails.
 */
export default function Contacts() {
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [favOnly, setFavOnly] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showPhoneImport, setShowPhoneImport] = useState(false);
  const [memberDrawer, setMemberDrawer] = useState(null); // contact whose linked_member we're viewing

  const refresh = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (groupFilter) params.set("group", groupFilter);
    if (favOnly) params.set("favourites_only", "true");
    const [r1, r2] = await Promise.all([
      api.get(`/contacts${params.toString() ? "?" + params : ""}`),
      api.get("/contacts/groups"),
    ]);
    setItems(r1.data || []);
    setGroups(r2.data || []);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [q, groupFilter, favOnly]);

  const toggleSel = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const toggleFav = async (c) => {
    try {
      await api.put(`/contacts/${c.id}`, { is_favourite: !c.is_favourite });
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name} from your contacts?`)) return;
    try {
      await api.delete(`/contacts/${c.id}`);
      toast.success("Contact removed");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const startCompose = () => {
    if (selected.size === 0) {
      toast.error("Pick at least one contact first");
      return;
    }
    setShowCompose(true);
  };

  const selectedContacts = useMemo(
    () => items.filter((c) => selected.has(c.id)),
    [items, selected],
  );

  return (
    <div className="space-y-5 pb-12" data-testid="contacts-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-overline">Contacts</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Your network</h1>
          <p className="text-ink-600 text-sm mt-1">Keep a private list of musicians, fixers and contacts. Email them in one go.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowPhoneImport(true)} className="btn-secondary text-sm" data-testid="contacts-import-phone-btn"><Smartphone size={16} /> Import from phone</button>
          <button onClick={() => setShowImport(true)} className="btn-secondary text-sm" data-testid="contacts-import-btn"><Upload size={16} /> Paste-import</button>
          <button onClick={() => setShowNew(true)} className="btn-primary text-sm" data-testid="contacts-new-btn"><Plus size={16} /> Add contact</button>
        </div>
      </div>

      {/* Filters */}
      <div className="card-base p-4 space-y-3">
        <div className="flex gap-2 items-center">
          <Search size={16} className="text-ink-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, instrument…"
            className="input-field !py-1.5 flex-1"
            data-testid="contacts-search-input"
          />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={() => setFavOnly(!favOnly)}
            className={`pill ${favOnly ? "bg-amber-soft text-amber-deep" : "bg-ivory-100 text-ink-700"}`}
            data-testid="contacts-favourites-toggle"
          >
            <Star size={12} className={favOnly ? "fill-amber-deep" : ""} /> Favourites
          </button>
          {groups.map((g) => (
            <button
              key={g}
              onClick={() => setGroupFilter(groupFilter === g ? "" : g)}
              className={`pill ${groupFilter === g ? "bg-sage-500 text-white" : "bg-ivory-100 text-ink-700"}`}
              data-testid={`contacts-group-${g}`}
            >
              <Tag size={12} /> {g}
            </button>
          ))}
        </div>
      </div>

      {/* Selection bar */}
      {selected.size > 0 && (
        <div className="rounded-2xl bg-sage-500 text-white px-4 py-3 flex items-center justify-between gap-3 shadow-card" data-testid="contacts-selection-bar">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <div className="flex gap-2">
            <button onClick={() => setSelected(new Set())} className="text-sm underline opacity-90 hover:opacity-100" data-testid="contacts-clear-selection">Clear</button>
            <button onClick={startCompose} className="bg-white text-sage-700 rounded-full px-3 py-1.5 text-sm font-medium inline-flex items-center gap-1.5" data-testid="contacts-compose-btn">
              <Send size={14} /> Email {selected.size === 1 ? "them" : "them all"}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {items.length === 0 ? (
        <div className="card-base p-8 text-center" data-testid="contacts-empty">
          <Mail className="mx-auto text-sage-500" size={28} />
          <div className="mt-3 font-heading font-semibold">No contacts yet.</div>
          <p className="text-sm text-ink-600 mt-1">Build your rolodex. These contacts power external dep broadcasts when AMDS hasn't found cover from members.</p>
          <button onClick={() => setShowNew(true)} className="btn-primary text-sm mt-4" data-testid="contacts-empty-new-btn"><Plus size={16} /> Add your first contact</button>
        </div>
      ) : (
        <ul className="grid sm:grid-cols-2 gap-3">
          {items.map((c) => (
            <li key={c.id}>
              <article
                className={`card-base p-4 cursor-pointer transition ${selected.has(c.id) ? "ring-2 ring-sage-500" : "hover:-translate-y-[1px]"}`}
                onClick={() => toggleSel(c.id)}
                data-testid={`contact-${c.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold inline-flex items-center gap-1.5 flex-wrap">
                      {c.is_favourite && <Star size={14} className="fill-amber-deep text-amber-deep" />}
                      {c.name}
                      {c.linked_user_id && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setMemberDrawer(c); }}
                          className="pill bg-sage-100 text-sage-700 hover:bg-sage-200 !text-[10px] cursor-pointer"
                          title="View AMDS profile snapshot"
                          data-testid={`contact-amds-pill-${c.id}`}
                        >
                          <UserCheck size={10} /> ✓ AMDS Member
                        </button>
                      )}
                    </div>
                    <div className="text-xs text-ink-500 truncate mt-0.5">{c.email}</div>
                    {c.instrument && <div className="text-xs text-ink-600 mt-0.5">{c.instrument}</div>}
                    {c.relationship && <div className="text-xs text-sage-700 mt-0.5">{c.relationship}</div>}
                    {c.groups.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {c.groups.map((g) => <span key={g} className="pill bg-ivory-100 text-ink-600 !text-[10px]">{g}</span>)}
                      </div>
                    )}
                    {(c.private_notes || c.notes) && <p className="text-xs text-ink-500 mt-2 line-clamp-2">{c.private_notes || c.notes}</p>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={(e) => { e.stopPropagation(); toggleFav(c); }} className="btn-ghost !p-1.5" title="Favourite" data-testid={`contact-fav-${c.id}`}>
                      <Star size={14} className={c.is_favourite ? "fill-amber-deep text-amber-deep" : ""} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setEditing(c); }} className="btn-ghost !p-1.5" title="Edit" data-testid={`contact-edit-${c.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); remove(c); }} className="btn-ghost !p-1.5 text-terracotta-600" title="Remove" data-testid={`contact-delete-${c.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {selected.has(c.id) && (
                  <div className="mt-2 text-xs text-sage-700 inline-flex items-center gap-1"><Check size={12} /> Selected</div>
                )}
              </article>
            </li>
          ))}
        </ul>
      )}

      {showNew && <ContactForm onClose={() => { setShowNew(false); refresh(); }} groupsExisting={groups} />}
      {editing && <ContactForm contact={editing} onClose={() => { setEditing(null); refresh(); }} groupsExisting={groups} />}
      {showCompose && <ComposeModal contacts={selectedContacts} onClose={(sent) => { setShowCompose(false); if (sent) { setSelected(new Set()); refresh(); } }} />}
      {showImport && <ImportModal onClose={() => { setShowImport(false); refresh(); }} />}
      <ImportContactsModal
        open={showPhoneImport}
        onClose={() => setShowPhoneImport(false)}
        onImported={() => refresh()}
      />
      {memberDrawer && <MemberDrawer contact={memberDrawer} onClose={() => setMemberDrawer(null)} />}
    </div>
  );
}


// ---------- ContactForm (new + edit) ----------

function ContactForm({ contact, onClose, groupsExisting }) {
  const isEdit = !!contact;
  const [form, setForm] = useState(contact ? {
    name: contact.name || "",
    email: contact.email || "",
    instrument: contact.instrument || "",
    relationship: contact.relationship || "",
    recommended_by: contact.recommended_by || "",
    first_worked_together: contact.first_worked_together || "",
    private_notes: contact.private_notes || contact.notes || "",
    is_favourite: !!contact.is_favourite,
    groups: contact.groups || [],
  } : {
    name: "", email: "", instrument: "",
    relationship: "", recommended_by: "", first_worked_together: "",
    private_notes: "",
    is_favourite: false, groups: [],
  });
  const [groupDraft, setGroupDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const addGroup = (g) => {
    const v = (g || groupDraft).trim();
    if (!v) return;
    if (!form.groups.includes(v)) setForm({ ...form, groups: [...form.groups, v] });
    setGroupDraft("");
  };

  const removeGroup = (g) => setForm({ ...form, groups: form.groups.filter((x) => x !== g) });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name, email: form.email,
        instrument: form.instrument || null,
        relationship: form.relationship || null,
        recommended_by: form.recommended_by || null,
        first_worked_together: form.first_worked_together || null,
        private_notes: form.private_notes || null,
        is_favourite: !!form.is_favourite, groups: form.groups,
      };
      if (isEdit) {
        await api.put(`/contacts/${contact.id}`, payload);
        toast.success("Contact updated");
      } else {
        await api.post("/contacts", payload);
        toast.success("Contact added");
      }
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-ink-900/40 p-2" onClick={onClose} data-testid="contact-form-modal">
      <div className="bg-white w-full max-w-md rounded-2xl sm:rounded-3xl p-6 pb-32 sm:pb-6 shadow-card max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-semibold">{isEdit ? "Edit contact" : "Add contact"}</h3>
          <button onClick={onClose} className="btn-ghost"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input className="input-field" required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-form-name" />
          <input className="input-field" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-form-email" />
          <input className="input-field" placeholder="Instrument (optional)" value={form.instrument || ""} onChange={(e) => setForm({ ...form, instrument: e.target.value })} data-testid="contact-form-instrument" />

          <div className="pt-2 border-t border-ivory-200">
            <div className="label-overline mb-2 inline-flex items-center gap-1.5"><ShieldCheck size={12} /> Private notes (only you see these)</div>
            <div className="grid grid-cols-2 gap-2">
              <input className="input-field" placeholder="Relationship (e.g. Section leader)" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} data-testid="contact-form-relationship" maxLength={120} />
              <input className="input-field" placeholder="Recommended by" value={form.recommended_by} onChange={(e) => setForm({ ...form, recommended_by: e.target.value })} data-testid="contact-form-recommended-by" maxLength={120} />
            </div>
            <label className="text-xs text-ink-500 block mt-2 mb-1">First worked together</label>
            <input type="date" className="input-field" value={form.first_worked_together} onChange={(e) => setForm({ ...form, first_worked_together: e.target.value })} data-testid="contact-form-first-worked" />
            <textarea rows={3} className="input-field mt-2" placeholder="Private notes (only visible to you)" value={form.private_notes || ""} onChange={(e) => setForm({ ...form, private_notes: e.target.value })} data-testid="contact-form-private-notes" maxLength={2000} />
          </div>

          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!form.is_favourite} onChange={(e) => setForm({ ...form, is_favourite: e.target.checked })} data-testid="contact-form-favourite" />
            Mark as favourite
          </label>

          <div>
            <label className="label-overline block mb-2">Groups</label>
            <div className="flex flex-wrap gap-1 mb-2">
              {form.groups.map((g) => (
                <span key={g} className="pill bg-sage-100 text-sage-700 inline-flex items-center gap-1">
                  {g}
                  <button type="button" onClick={() => removeGroup(g)} className="text-sage-600 hover:text-sage-800"><X size={10} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                className="input-field !py-1.5"
                placeholder="Add a group (e.g. First Violins)"
                value={groupDraft}
                onChange={(e) => setGroupDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addGroup(); } }}
                data-testid="contact-form-group-input"
              />
              <button type="button" onClick={() => addGroup()} className="btn-secondary !py-1.5" data-testid="contact-form-group-add">Add</button>
            </div>
            {groupsExisting.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {groupsExisting.filter((g) => !form.groups.includes(g)).map((g) => (
                  <button type="button" key={g} onClick={() => addGroup(g)} className="pill bg-ivory-100 text-ink-600 hover:bg-ivory-200">+ {g}</button>
                ))}
              </div>
            )}
          </div>

          <button disabled={busy} className="btn-primary w-full justify-center" data-testid="contact-form-submit">{busy ? "Saving…" : (isEdit ? "Update" : "Add contact")}</button>
        </form>
      </div>
    </div>
  );
}


// ---------- MemberDrawer (privacy-safe AMDS profile snapshot) ----------

function MemberDrawer({ contact, onClose }) {
  const lm = contact.linked_member || {};
  const memberSince = lm.member_since ? new Date(lm.member_since).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : null;
  const availLabel = {
    available: { text: "Available today", cls: "bg-sage-100 text-sage-700" },
    busy: { text: "Busy today", cls: "bg-terracotta-100 text-terracotta-700" },
    tentative: { text: "Tentative today", cls: "bg-amber-soft text-amber-deep" },
    unknown: { text: "No status today", cls: "bg-ivory-100 text-ink-600" },
  }[lm.availability_today || "unknown"];

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-ink-900/40 p-2" onClick={onClose} data-testid="member-drawer">
      <div className="bg-white w-full max-w-md rounded-2xl sm:rounded-3xl p-6 shadow-card max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl font-semibold inline-flex items-center gap-2">
            <UserCheck className="text-sage-600" size={20} />
            AMDS Member
          </h3>
          <button onClick={onClose} className="btn-ghost" data-testid="member-drawer-close"><X size={18} /></button>
        </div>
        <div className="text-sm text-ink-700">
          <div className="font-medium">{contact.name}</div>
          <div className="text-xs text-ink-500">{contact.email}</div>
        </div>
        <div className="mt-4 space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <Music2 size={14} className="text-ink-400" />
            <span className="text-ink-600">Instrument:</span>
            <span className="text-ink-800 font-medium" data-testid="member-instrument">{lm.instrument || "—"}</span>
          </div>
          <div className="flex items-center gap-2">
            <CalendarClock size={14} className="text-ink-400" />
            <span className="text-ink-600">Member since:</span>
            <span className="text-ink-800 font-medium" data-testid="member-since">{memberSince || "—"}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`pill ${availLabel.cls}`} data-testid="member-availability">{availLabel.text}</span>
          </div>
        </div>
        <div className="mt-5 p-3 rounded-xl bg-ivory-100 text-xs text-ink-600 inline-flex items-start gap-2">
          <ShieldCheck size={14} className="text-sage-600 mt-0.5 shrink-0" />
          <span>Privacy first — you can only see instrument, member-since and today's availability. Diary details always stay private.</span>
        </div>
      </div>
    </div>
  );
}


// ---------- ComposeModal ----------

function ComposeModal({ contacts, onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and message both needed");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/contacts/email", {
        contact_ids: contacts.map((c) => c.id),
        subject, body,
      });
      toast.success(`Email sent to ${data.sent}`);
      onClose(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-ink-900/40 p-2" onClick={() => onClose(false)} data-testid="compose-modal">
      <div className="bg-white w-full max-w-lg rounded-2xl sm:rounded-3xl p-6 pb-32 sm:pb-6 shadow-card max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-semibold">Email {contacts.length} contact{contacts.length === 1 ? "" : "s"}</h3>
          <button onClick={() => onClose(false)} className="btn-ghost"><X size={18} /></button>
        </div>
        <div className="mb-3 p-3 bg-ivory-100 rounded-xl text-xs text-ink-600">
          <div className="font-medium mb-1">Recipients</div>
          <div className="flex flex-wrap gap-1">
            {contacts.map((c) => <span key={c.id} className="pill bg-white text-ink-700">{c.name}</span>)}
          </div>
          <p className="mt-2 text-ink-500">
            They'll see *"From you via AMDS"* and replies will come back to your email.
            Each gets a one-click unsubscribe link.
          </p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input
            className="input-field" required maxLength={200}
            placeholder="Subject"
            value={subject} onChange={(e) => setSubject(e.target.value)}
            data-testid="compose-subject"
          />
          <textarea
            rows={8} className="input-field"
            required maxLength={20000}
            placeholder="Hi all, are you around for Brahms 4 on Sat 21 June? Single rehearsal at 18:00…"
            value={body} onChange={(e) => setBody(e.target.value)}
            data-testid="compose-body"
          />
          <button disabled={busy} className="btn-primary w-full justify-center" data-testid="compose-send-btn">
            {busy ? "Sending…" : (<><Send size={16} /> Send</>)}
          </button>
        </form>
      </div>
    </div>
  );
}


// ---------- ImportModal ----------

function ImportModal({ onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/contacts/import", { text });
      const skipMsg = data.skipped_count > 0 ? ` (${data.skipped_count} skipped)` : "";
      toast.success(`Imported ${data.imported} contacts${skipMsg}`);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-ink-900/40 p-2" onClick={onClose} data-testid="import-modal">
      <div className="bg-white w-full max-w-md rounded-2xl sm:rounded-3xl p-6 pb-32 sm:pb-6 shadow-card max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-semibold">Paste contacts</h3>
          <button onClick={onClose} className="btn-ghost"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm text-ink-600">One contact per line. Anything goes — we'll find the email and use the rest as the name.</p>
          <pre className="text-xs bg-ivory-100 rounded-xl p-3 overflow-x-auto whitespace-pre-wrap text-ink-600">{"Anna Lim, anna@example.com, Violin\nMaria Singh; maria@example.com; Viola\nrob@example.com"}</pre>
          <textarea
            rows={10} className="input-field font-mono text-sm"
            placeholder="Paste your list here…"
            value={text} onChange={(e) => setText(e.target.value)}
            data-testid="import-textarea"
          />
          <button disabled={busy || !text.trim()} className="btn-primary w-full justify-center" data-testid="import-submit-btn">
            {busy ? "Importing…" : "Import"}
          </button>
        </form>
      </div>
    </div>
  );
}
